'use client';

import { useEffect, useState } from 'react';
import { Download, Info } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import type { BoardType } from '@/stores/useBoardEditorStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import { InstallerVersionItems } from '@/types/installer';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import IconAdaptive from '@/components/IconAdaptive';
import SectionTitle from '@/components/SectionTitle';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import BoardSelectorManual, {
    BoardValue,
} from '@/(routers)/boards/editor/components/modal/BoardSelectorManualWebSerialApi';

interface BoardSelectorAutoWebSerialApiProps {
    value?: BoardValue;
    onChange: (v: BoardType | null) => void;
    isDisabled?: boolean;
    onConnect?: (baud: number, board: 'orange' | 'blue') => void;
}

export default function BoardSelectorAutoWebSerialApi({
    onChange,
    isDisabled,
    onConnect,
}: BoardSelectorAutoWebSerialApiProps) {
    const [local, setLocal] = useState<BoardValue>(null);
    const closeModal = useModalStore((s) => s.closeModal);
    const { handleError, clearError } = useHandleError();
    const [currentInstallerOs, setCurrentInstallerOs] = useState<InstallerVersionItems[]>();
    const [selectedInstallerOs, setSelectedInstallerOs] = useState<InstallerVersionItems | null>(null);
    const showToastAsync = useToastStore((s) => s.showToastAsync);

    const osType = useBoardEditorStore((s) => s.osType);
    // Chromebookではブリッジという概念自体が無いので案内しない。
    // このモーダルがWindows/Macで開かれるのは「ブリッジ未接続」の場合のみ。
    const showBridgeInstaller = osType !== 'chromebook';

    const handlePick = (v: BoardValue) => {
        setLocal(v);
        onChange(v);
        if (v) closeModal?.();
    };

    useEffect(() => {
        if (!currentInstallerOs?.length) return;
        if (!osType) return;

        setSelectedInstallerOs(currentInstallerOs.find((item) => item.os === osType) ?? null);
    }, [currentInstallerOs, osType]);

    useEffect(() => {
        if (!showBridgeInstaller) return;
        let cancelled = false;
        (async () => {
            clearError();
            try {
                const response = await apiFetch(`/installers/bridge`, { method: 'GET' });
                if (cancelled) return;
                setCurrentInstallerOs(response.schoomy_bridge_installer?.current_installers);
            } catch (err: unknown) {
                if (cancelled) return;
                handleError(err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showBridgeInstaller]);

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title="Select SchooMyBoard" />
                </div>
            </div>

            <div className="flex w-full items-center pt-4 pb-2">
                <div className="flex flex-grow justify-start items-start w-full font-notojp font-medium text-sm text-color-base">
                    <div className="flex flex-col justify-start items-start">
                        使用するスクーミーボードを選択してください。
                    </div>
                </div>
            </div>
            <div className="flex flex-col w-full">
                <div className="flex justify-start items-center w-full py-2">
                    <FormFieldLabel title="手動選択" />
                </div>
                <div className="flex w-full items-center">
                    <div className="flex flex-grow justify-start items-start w-full font-notojp font-medium text-xs text-color-base">
                        <div className="flex justify-start items-start">
                            <IconAdaptive
                                Icon={Info}
                                strokeWidth={2.0}
                                baseSize={20}
                                breakpoints={{ md: 20 }}
                                className="mr-[8px]"
                            />
                        </div>
                        <div className="flex items-center mt-[1.5px]">該当するボードを選択してください。</div>
                    </div>
                </div>
                <div className="section-spacer-inner-y">
                    <BoardSelectorManual
                        value={local}
                        onChange={handlePick}
                        isDisabled={isDisabled}
                        onConnect={onConnect}
                    />
                </div>
            </div>

            {showBridgeInstaller && (
                <div className="flex flex-col w-full mt-4">
                    <div className="section-spacer-inner-y" />
                    <div className="flex w-full items-center border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex flex-grow justify-start items-start font-notojp font-medium text-xs text-color-base">
                            スクーミーブリッジをインストールすると、USB/Bluetooth接続の自動検出やペアリング機能が使えるようになります。
                        </div>
                        <div className="flex items-center ml-4 flex-shrink-0">
                            <ButtonGAdaptive
                                icon={Download}
                                label="ダウンロード"
                                size="xs"
                                onClick={async () => {
                                    const url = selectedInstallerOs?.installer_url;
                                    if (!url) {
                                        await showToastAsync(
                                            'ダウンロード可能なインストーラーがありません',
                                            'error',
                                            3000,
                                        );
                                        return;
                                    }
                                    // ButtonGAdaptiveは<button>を描画するため、<a>で入れ子にすると
                                    // インタラクティブ要素の無効なネストになる(hrefが無い間はaが
                                    // フォーカス不可になり、キーボード操作でトーストにも到達できない
                                    // 問題があった)。代わりに一時的なaを作ってクリックする。
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = '';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
